import React, { useEffect, useRef, useState } from 'react';
import { Mail, Linkedin, Copy, Check, MapPin, Phone, Instagram, Send, Loader2 } from 'lucide-react';
import axios from 'axios';
import { designer, ui } from '../data/mock';
import Footer from '../components/Footer';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [copyState, setCopyState] = useState('idle'); // 'idle' | 'copied' | 'error'
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [formState, setFormState] = useState('idle'); // 'idle' | 'sending' | 'sent' | 'error'
  const [formError, setFormError] = useState('');
  const refs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((e) => e.isIntersecting && e.target.classList.add('visible'));
    }, { threshold: 0.2 });
    refs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const copyEmail = async () => {
    if (!navigator.clipboard) {
      setCopyState('error');
      setTimeout(() => setCopyState('idle'), 2400);
      return;
    }
    try {
      await navigator.clipboard.writeText(designer.email);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 1800);
    } catch (err) {
      setCopyState('error');
      setTimeout(() => setCopyState('idle'), 2400);
    }
  };

  const updateField = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (formState === 'error') { setFormState('idle'); setFormError(''); }
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (formState === 'sending') return;
    // Basic client-side sanity check
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setFormState('error');
      setFormError('Udfyld venligst alle felter.');
      return;
    }
    setFormState('sending');
    setFormError('');
    try {
      await axios.post(`${API_BASE}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim()
      });
      setFormState('sent');
      setForm({ name: '', email: '', message: '' });
      setTimeout(() => setFormState('idle'), 4500);
    } catch (err) {
      setFormState('error');
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail) && detail[0]?.msg) {
        // Pydantic validation error
        const loc = detail[0].loc?.[1];
        if (loc === 'email') setFormError('Indtast venligst en gyldig emailadresse.');
        else setFormError(detail[0].msg);
      } else if (typeof detail === 'string') {
        setFormError(detail);
      } else {
        setFormError('Beskeden kunne ikke sendes. Prøv igen om et øjeblik.');
      }
    }
  };

  return (
    <main className="min-h-screen w-full relative overflow-hidden" style={{ background: 'var(--bg-dark)' }}>
      {/* Animated atmospheric background */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute -top-40 -left-40 w-[60vw] h-[60vw] rounded-full opacity-30 blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(179,84,62,0.4) 0%, transparent 70%)', animation: 'drift1 18s ease-in-out infinite' }}
        />
        <div
          className="absolute top-1/2 -right-40 w-[55vw] h-[55vw] rounded-full opacity-25 blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(232,179,60,0.32) 0%, transparent 70%)', animation: 'drift2 22s ease-in-out infinite' }}
        />
        <div
          className="absolute bottom-0 left-1/3 w-[45vw] h-[45vw] rounded-full opacity-20 blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(62,95,76,0.4) 0%, transparent 70%)', animation: 'drift3 26s ease-in-out infinite' }}
        />
        <div
          className="absolute top-1/4 left-1/2 w-[40vw] h-[40vw] rounded-full opacity-20 blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(74,107,130,0.38) 0%, transparent 70%)', animation: 'drift4 24s ease-in-out infinite' }}
        />
      </div>

      <style>{`
        @keyframes drift1 { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(8vw,6vh) scale(1.1); } }
        @keyframes drift2 { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(-6vw,-8vh) scale(1.15); } }
        @keyframes drift3 { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(4vw,-10vh) scale(0.95); } }
        @keyframes drift4 { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(-9vw,9vh) scale(1.08); } }
      `}</style>

      <section className="relative pt-40 pb-32 px-8 md:px-[90px]">
        <div className="font-label text-[0.72rem] text-[var(--terracotta)] mb-10">
          ————  Kontakt  /  Åben for samarbejder
        </div>
        <h1
          ref={(el) => (refs.current[0] = el)}
          className="reveal font-display text-[clamp(3.2rem,11vw,11rem)] text-[var(--cream)] leading-[0.9] max-w-6xl"
        >
          Lad os<br />
          <span style={{ fontStyle: 'italic', fontWeight: 300, color: 'var(--terracotta)' }}>tale</span> sammen.
        </h1>
        <p
          ref={(el) => (refs.current[1] = el)}
          className="reveal font-body text-[var(--cream)]/60 text-lg md:text-xl mt-10 max-w-xl leading-relaxed"
          style={{ transitionDelay: '120ms' }}
        >
          Hvad enten det handler om en visuel identitet, en kampagne eller en stille samtale om en idé, du stadig former — vil jeg meget gerne høre fra dig.
        </p>
      </section>

      <section className="relative px-8 md:px-[90px] pb-32">
        <div className="grid md:grid-cols-12 gap-8">
          {/* Email card — large */}
          <div
            ref={(el) => (refs.current[2] = el)}
            className="reveal md:col-span-7 border border-[var(--cream)]/15 hover:border-[var(--terracotta)] transition-colors duration-500 p-8 md:p-12 group"
            style={{ background: 'rgba(58, 42, 40, 0.35)', backdropFilter: 'blur(8px)' }}
          >
            <div className="flex items-center gap-3 font-label text-[0.66rem] text-[var(--amber)] mb-8">
              <Mail size={14} />
              <span>{ui.primaryEmail}</span>
            </div>
            <a
              href={`mailto:${designer.email}`}
              className="font-display text-[clamp(1.4rem,3.6vw,2.9rem)] text-[var(--cream)] group-hover:text-[var(--terracotta)] transition-colors duration-500 break-all block"
            >
              {designer.email}
            </a>
            <button
              onClick={copyEmail}
              aria-live="polite"
              className={`mt-10 inline-flex items-center gap-3 font-label text-[0.7rem] transition-colors duration-300 ${copyState === 'error' ? 'text-[var(--terracotta)]' : 'text-[var(--cream)]/60 hover:text-[var(--terracotta)]'}`}
            >
              {copyState === 'copied' && (
                <>
                  <Check size={14} />
                  <span>{ui.copied}</span>
                </>
              )}
              {copyState === 'error' && (
                <>
                  <Copy size={14} />
                  <span>Kopiering mislykkedes — brug mailto i stedet</span>
                </>
              )}
              {copyState === 'idle' && (
                <>
                  <Copy size={14} />
                  <span>{ui.copyEmail}</span>
                </>
              )}
            </button>
          </div>

          {/* Phone card */}
          <a
            ref={(el) => (refs.current[3] = el)}
            href={designer.phoneHref}
            className="reveal md:col-span-5 border border-[var(--cream)]/15 hover:border-[var(--terracotta)] transition-colors duration-500 p-8 group flex flex-col justify-between"
            style={{ background: 'rgba(58, 42, 40, 0.35)', backdropFilter: 'blur(8px)', transitionDelay: '60ms' }}
          >
            <div className="flex items-center gap-3 font-label text-[0.66rem] text-[var(--amber)] mb-8">
              <Phone size={14} />
              <span>{ui.phone}</span>
            </div>
            <div>
              <div className="font-display text-[clamp(1.6rem,3vw,2.4rem)] text-[var(--cream)] group-hover:text-[var(--terracotta)] transition-colors duration-500">
                {designer.phone}
              </div>
              <div className="font-body text-[var(--cream)]/55 mt-3 text-sm">
                Hverdage 09:00 — 17:00
              </div>
            </div>
          </a>

          {/* LinkedIn card */}
          <a
            ref={(el) => (refs.current[4] = el)}
            href={designer.linkedIn}
            target="_blank"
            rel="noopener noreferrer"
            className="reveal md:col-span-4 border border-[var(--cream)]/15 hover:border-[var(--terracotta)] transition-colors duration-500 p-8 group"
            style={{ background: 'rgba(58, 42, 40, 0.35)', backdropFilter: 'blur(8px)', transitionDelay: '120ms' }}
          >
            <div className="flex items-center gap-3 font-label text-[0.66rem] text-[var(--amber)] mb-8">
              <Linkedin size={14} />
              <span>{ui.connect}</span>
            </div>
            <div className="font-display text-3xl text-[var(--cream)] group-hover:text-[var(--terracotta)] transition-colors duration-500">
              LinkedIn
            </div>
            <div className="font-body text-[var(--cream)]/55 mt-4 text-sm break-all">
              {designer.linkedInHandle}
            </div>
          </a>

          {/* Instagram card */}
          <a
            ref={(el) => (refs.current[5] = el)}
            href={designer.instagramURL}
            target="_blank"
            rel="noopener noreferrer"
            className="reveal md:col-span-4 border border-[var(--cream)]/15 hover:border-[var(--terracotta)] transition-colors duration-500 p-8 group"
            style={{ background: 'rgba(58, 42, 40, 0.35)', backdropFilter: 'blur(8px)', transitionDelay: '180ms' }}
          >
            <div className="flex items-center gap-3 font-label text-[0.66rem] text-[var(--amber)] mb-8">
              <Instagram size={14} />
              <span>{ui.follow}</span>
            </div>
            <div className="font-display text-3xl text-[var(--cream)] group-hover:text-[var(--terracotta)] transition-colors duration-500">
              Instagram
            </div>
            <div className="font-body text-[var(--cream)]/55 mt-4 text-sm break-all">
              {designer.instagram}
            </div>
          </a>

          {/* Location card */}
          <div
            ref={(el) => (refs.current[6] = el)}
            className="reveal md:col-span-4 border border-[var(--cream)]/15 p-8"
            style={{ background: 'rgba(58, 42, 40, 0.35)', backdropFilter: 'blur(8px)', transitionDelay: '240ms' }}
          >
            <div className="flex items-center gap-3 font-label text-[0.66rem] text-[var(--amber)] mb-8">
              <MapPin size={14} />
              <span>{ui.located}</span>
            </div>
            <div className="font-display text-2xl text-[var(--cream)]">
              {designer.location}
            </div>
            <div className="font-body text-[var(--cream)]/55 mt-3 text-sm">
              Arbejder med kunder globalt.
            </div>
          </div>
        </div>
      </section>

      {/* Contact form section */}
      <section className="relative px-8 md:px-[90px] pb-40" data-testid="contact-form-section">
        <div className="grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-4">
            <div className="font-label text-[0.72rem] text-[var(--terracotta)] mb-6">
              ————  Skriv til mig
            </div>
            <h2
              ref={(el) => (refs.current[7] = el)}
              className="reveal font-display text-[clamp(2.2rem,4.8vw,4rem)] text-[var(--cream)] leading-[1] mb-8"
            >
              Fortæl mig<br />
              <span style={{ fontStyle: 'italic', fontWeight: 300, color: 'var(--amber)' }}>om projektet.</span>
            </h2>
            <p className="font-body text-[var(--cream)]/55 text-base leading-relaxed max-w-sm">
              En kort skitse, en drøm, eller blot en åben samtale. Jeg svarer normalt inden for et par hverdage.
            </p>
          </div>

          <form
            ref={(el) => (refs.current[8] = el)}
            onSubmit={submitForm}
            className="reveal md:col-span-8 border border-[var(--cream)]/15 p-8 md:p-12 space-y-8"
            style={{ background: 'rgba(58, 42, 40, 0.35)', backdropFilter: 'blur(8px)', transitionDelay: '80ms' }}
            data-testid="contact-form"
            noValidate
          >
            <div className="grid md:grid-cols-2 gap-8">
              <label className="block">
                <span className="font-label text-[0.66rem] text-[var(--amber)] block mb-3">Navn</span>
                <input
                  type="text"
                  value={form.name}
                  onChange={updateField('name')}
                  disabled={formState === 'sending'}
                  className="w-full bg-transparent border-b border-[var(--cream)]/25 focus:border-[var(--terracotta)] outline-none py-3 font-body text-[var(--cream)] text-lg transition-colors duration-300 placeholder:text-[var(--cream)]/30 disabled:opacity-50"
                  placeholder="Dit navn"
                  data-testid="contact-input-name"
                  autoComplete="name"
                  required
                />
              </label>
              <label className="block">
                <span className="font-label text-[0.66rem] text-[var(--amber)] block mb-3">Email</span>
                <input
                  type="email"
                  value={form.email}
                  onChange={updateField('email')}
                  disabled={formState === 'sending'}
                  className="w-full bg-transparent border-b border-[var(--cream)]/25 focus:border-[var(--terracotta)] outline-none py-3 font-body text-[var(--cream)] text-lg transition-colors duration-300 placeholder:text-[var(--cream)]/30 disabled:opacity-50"
                  placeholder="din@email.dk"
                  data-testid="contact-input-email"
                  autoComplete="email"
                  required
                />
              </label>
            </div>

            <label className="block">
              <span className="font-label text-[0.66rem] text-[var(--amber)] block mb-3">Besked</span>
              <textarea
                rows={5}
                value={form.message}
                onChange={updateField('message')}
                disabled={formState === 'sending'}
                className="w-full bg-transparent border-b border-[var(--cream)]/25 focus:border-[var(--terracotta)] outline-none py-3 font-body text-[var(--cream)] text-lg transition-colors duration-300 placeholder:text-[var(--cream)]/30 resize-none disabled:opacity-50"
                placeholder="Fortæl kort om projektet, tidsrammen og hvad du drømmer om..."
                data-testid="contact-input-message"
                required
              />
            </label>

            <div className="flex items-center justify-between flex-wrap gap-6 pt-4">
              <div className="min-h-[1.75rem] font-label text-[0.7rem]" aria-live="polite">
                {formState === 'sent' && (
                  <span className="text-[var(--amber)] inline-flex items-center gap-2" data-testid="contact-form-success">
                    <Check size={14} /> Tak — beskeden er sendt. Jeg vender tilbage snart.
                  </span>
                )}
                {formState === 'error' && (
                  <span className="text-[var(--terracotta)]" data-testid="contact-form-error">{formError}</span>
                )}
              </div>
              <button
                type="submit"
                disabled={formState === 'sending' || formState === 'sent'}
                className="inline-flex items-center gap-4 font-label text-[0.72rem] text-[var(--cream)] border border-[var(--cream)]/30 hover:border-[var(--terracotta)] hover:text-[var(--terracotta)] px-7 py-4 transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="contact-submit-button"
              >
                {formState === 'sending' ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Sender...
                  </>
                ) : formState === 'sent' ? (
                  <>
                    <Check size={14} /> Sendt
                  </>
                ) : (
                  <>
                    <Send size={14} /> Send besked
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default Contact;
